/**
 * Firebase Service Layer
 * 
 * This file provides a comprehensive service layer for all Firestore operations
 * in the Survivor fantasy league application. It includes CRUD operations for
 * all collections and sub-collections as defined in the data models.
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  getCountFromServer,
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot,
  writeBatch,
  runTransaction,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  USER_ROLES, 
  generateInvitationCode
} from '../models/dataModels';

// ============================================================================
// USERS SERVICE
// ============================================================================

export class RoleService {
  static collection = 'roles';

  /**
   * Get all roles
   * @returns {Promise<Array>} - Array of all roles
   */
  static async getAllRoles() {
    const querySnapshot = await getDocs(collection(db, this.collection));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  /**
   * Get role by ID
   * @param {string} roleId - Role document ID
   * @returns {Promise<Object>} - Role data
   */
  static async getRole(roleId) {
    const docRef = doc(db, this.collection, roleId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }

  /**
   * Get roles by IDs
   * @param {Array<string>} roleIds - Array of role IDs
   * @returns {Promise<Array>} - Array of role objects
   */
  static async getRolesByIds(roleIds) {
    if (!roleIds || roleIds.length === 0) return [];
    
    const roles = [];
    for (const roleId of roleIds) {
      const role = await this.getRole(roleId);
      if (role) {
        roles.push(role);
      }
    }
    return roles;
  }
}

export class UserService {
  static collection = 'users';

  /**
   * Create a new user document
   * @param {Object} userData - User data from Firebase Auth
   * @returns {Promise<string>} - User document ID
   */
  static async createUser(userData) {
    try {
      console.log('UserService.createUser called with:', userData);
      
      const userDoc = {
        email: userData.email,
        displayName: userData.displayName || userData.email.split('@')[0],
        roles: [USER_ROLES.PLAYER],
        teams: {},
        managedLeagues: [],
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      };

      console.log('User document to create:', userDoc);

      // Use the Firebase Auth UID as the document ID
      const docRef = doc(db, this.collection, userData.uid);
      await setDoc(docRef, userDoc);
      console.log('User document created successfully with ID:', userData.uid);
      return userData.uid;
    } catch (error) {
      console.error('UserService.createUser error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  /**
   * Get user by ID
   * @param {string} userId - User document ID
   * @returns {Promise<Object>} - User data
   */
  static async getUser(userId) {
    const docRef = doc(db, this.collection, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }

    /**
   * Get user by email
   * @param {string} email - User email
   * @returns {Promise<Object>} - User data
   */
  static async getUserByEmail(email) {
    const q = query(
      collection(db, this.collection),
      where('email', '==', email),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  }


  /**
   * Get all users
   * @returns {Promise<Array>} - Array of all users
   */
  static async getAllUsers() {
    const querySnapshot = await getDocs(collection(db, this.collection));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  /**
   * Create a new user with authentication (for admin use)
   * Uses a local server endpoint to avoid logging out the current user
   * @param {Object} userData - User data including email, password, displayName, roles
   * @returns {Promise<Object>} - Created user data
   */
  static async createUserWithAuth(userData) {
    try {
      // Call the local server endpoint to create user without affecting current session
      const response = await fetch('http://localhost:3001/api/create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user');
      }

      const result = await response.json();
      return result.user;
    } catch (error) {
      console.error('UserService.createUserWithAuth error:', error);
      throw error;
    }
  }

  /**
   * Update user data
   * @param {string} userId - User document ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  static async updateUser(userId, updates) {
    try {
      const docRef = doc(db, this.collection, userId);
      
      // Check if document exists first
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error(`User document with ID ${userId} does not exist`);
      }
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('UserService.updateUser error:', error);
      throw error;
    }
  }

  /**
   * Add user to a team (legacy method - kept for backward compatibility)
   * @deprecated Use claimTeam instead. This method does not work with the nested teams structure.
   * @param {string} userId - User document ID
   * @param {string} leagueId - League ID
   * @param {string} teamId - Team ID
   * @returns {Promise<void>}
   */
  static async addUserToTeam(userId, leagueId, teamId) {
    console.warn('addUserToTeam is deprecated. Use claimTeam(userId, teamId, leagueId, seasonId) instead.');
    throw new Error('addUserToTeam is deprecated. Use claimTeam(userId, teamId, leagueId, seasonId) instead. SeasonId is required.');
  }

  /**
   * Remove user from a team (legacy method - kept for backward compatibility)
   * @deprecated Use unclaimTeam instead. This method does not work with the nested teams structure.
   * @param {string} userId - User document ID
   * @param {string} leagueId - League ID
   * @param {string} teamId - Team ID
   * @returns {Promise<void>}
   */
  static async removeUserFromTeam(userId, leagueId, teamId) {
    console.warn('removeUserFromTeam is deprecated. Use unclaimTeam(userId, teamId, leagueId, seasonId) instead.');
    throw new Error('removeUserFromTeam is deprecated. Use unclaimTeam(userId, teamId, leagueId, seasonId) instead. SeasonId is required.');
  }

  /**
   * Claim multiple teams for a user in a single atomic operation
   * Prevents race conditions when claiming multiple teams simultaneously
   * @param {string} userId - User document ID
   * @param {Array<string>} teamIds - Array of team IDs to claim
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<void>}
   */
  static async claimTeams(userId, teamIds, leagueId, seasonId) {
    const validTeamIds = (teamIds || []).filter(Boolean);

    if (!userId || validTeamIds.length === 0 || !leagueId || !seasonId) {
      throw new Error('userId, teamIds (non-empty array), leagueId, and seasonId are required');
    }

    const docRef = doc(db, this.collection, userId);
    
    // Get current user data
    const userDoc = await getDoc(docRef);
    if (!userDoc.exists()) {
      throw new Error(`User document with ID ${userId} does not exist`);
    }

    const userData = userDoc.data();
    const teams = userData.teams || {};
    
    // Check if teams are already claimed (handle both old and new formats)
    const leagueData = teams[leagueId] || {};
    const seasonData = leagueData[seasonId];
    
    // Normalize seasonData to array format for multiple teams support
    let teamsArray = [];
    if (seasonData) {
      if (Array.isArray(seasonData)) {
        // Already in array format
        teamsArray = [...seasonData]; // Create a copy
      } else if (typeof seasonData === 'string') {
        // Old format: single team as string
        teamsArray = [{ teamId: seasonData, claimedAt: null }];
      } else if (seasonData.teamId) {
        // New format: single team as object
        teamsArray = [seasonData];
      }
    }
    
    // Filter out teams that are already claimed
    const existingTeamIds = teamsArray.map(t => {
      return typeof t === 'string' ? t : (t.teamId || null);
    }).filter(Boolean);
    
    const newTeamIds = validTeamIds.filter(teamId => !existingTeamIds.includes(teamId));
    
    if (newTeamIds.length === 0) {
      // All teams already claimed
      return;
    }
    
    // Add all new teams to array in a single operation
    const now = Timestamp.now();
    newTeamIds.forEach(teamId => {
      teamsArray.push({
        teamId: teamId,
        claimedAt: now
      });
    });
    
    // Update with array format (supports multiple teams per league-season)
    await updateDoc(docRef, {
      [`teams.${leagueId}.${seasonId}`]: teamsArray
    });

    // Keep team.players in sync so Firestore rules allow claimers to update the team
    const batch = writeBatch(db);
    for (const teamId of newTeamIds) {
      const teamRef = doc(db, 'leagues', leagueId, 'seasons', seasonId, 'teams', teamId);
      batch.update(teamRef, {
        players: arrayUnion(userId),
        updatedAt: serverTimestamp()
      });
    }
    await batch.commit();
  }

  /**
   * Claim a team for a user
   * Adds team to user's teams nested map structure with claimedAt timestamp
   * @param {string} userId - User document ID
   * @param {string} teamId - Team ID
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<void>}
   */
  static async claimTeam(userId, teamId, leagueId, seasonId) {
    if (!userId || !teamId || !leagueId || !seasonId) {
      throw new Error('userId, teamId, leagueId, and seasonId are required');
    }

    const docRef = doc(db, this.collection, userId);
    
    // Get current user data to check if team is already claimed
    const userDoc = await getDoc(docRef);
    if (!userDoc.exists()) {
      throw new Error(`User document with ID ${userId} does not exist`);
    }

    const userData = userDoc.data();
    const teams = userData.teams || {};
    
    // Check if team is already claimed (handle both old and new formats)
    const leagueData = teams[leagueId] || {};
    const seasonData = leagueData[seasonId];
    
    // Normalize seasonData to array format for multiple teams support
    let teamsArray = [];
    if (seasonData) {
      if (Array.isArray(seasonData)) {
        // Already in array format
        teamsArray = seasonData;
      } else if (typeof seasonData === 'string') {
        // Old format: single team as string
        teamsArray = [{ teamId: seasonData, claimedAt: null }];
      } else if (seasonData.teamId) {
        // New format: single team as object
        teamsArray = [seasonData];
      }
    }
    
    // Check if team is already in the array
    const existingTeam = teamsArray.find(t => {
      const existingTeamId = typeof t === 'string' ? t : (t.teamId || null);
      return existingTeamId === teamId;
    });
    
    if (existingTeam) {
      // Already claimed, no need to update
      return;
    }
    
    // Add new team to array
    // Note: serverTimestamp() cannot be used inside arrays in Firestore
    // Use client-side timestamp instead
    teamsArray.push({
      teamId: teamId,
      claimedAt: Timestamp.now()
    });
    
    // Update with array format (supports multiple teams per league-season)
    await updateDoc(docRef, {
      [`teams.${leagueId}.${seasonId}`]: teamsArray
    });

    // Keep team.players in sync so Firestore rules allow claimers to update the team
    const teamRef = doc(db, 'leagues', leagueId, 'seasons', seasonId, 'teams', teamId);
    await updateDoc(teamRef, {
      players: arrayUnion(userId),
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Unclaim a team for a user
   * Removes team from user's teams nested map structure
   * Supports both single team and array formats
   * @param {string} userId - User document ID
   * @param {string} teamId - Team ID
   * @param {string} leagueId - League ID (optional, for faster lookup)
   * @param {string} seasonId - Season ID (optional, for faster lookup)
   * @returns {Promise<void>}
   */
  static async unclaimTeam(userId, teamId, leagueId = null, seasonId = null) {
    if (!userId || !teamId) {
      throw new Error('userId and teamId are required');
    }

    const docRef = doc(db, this.collection, userId);
    
    // Get current user data
    const userDoc = await getDoc(docRef);
    if (!userDoc.exists()) {
      throw new Error(`User document with ID ${userId} does not exist`);
    }

    const userData = userDoc.data();
    const teams = userData.teams || {};
    
    // If leagueId and seasonId provided, remove directly
    if (leagueId && seasonId) {
      const leagueData = teams[leagueId];
      if (leagueData && leagueData[seasonId]) {
        const seasonData = leagueData[seasonId];
        
        // Handle array format (multiple teams per league-season)
        if (Array.isArray(seasonData)) {
          // Filter out the team to unclaim
          const updatedArray = seasonData.filter(t => {
            const existingTeamId = typeof t === 'string' ? t : (t.teamId || null);
            return existingTeamId !== teamId;
          });
          
          if (updatedArray.length === 0) {
            // No teams left, remove the season entry
            const updatedLeagueData = { ...leagueData };
            delete updatedLeagueData[seasonId];
            
            // If league has no more seasons, remove the league entry
            if (Object.keys(updatedLeagueData).length === 0) {
              const updatedTeams = { ...teams };
              delete updatedTeams[leagueId];
              await updateDoc(docRef, { teams: updatedTeams });
            } else {
              await updateDoc(docRef, {
                [`teams.${leagueId}`]: updatedLeagueData
              });
            }
          } else {
            // Update with filtered array
            await updateDoc(docRef, {
              [`teams.${leagueId}.${seasonId}`]: updatedArray
            });
          }
        } else {
          // Handle single team format (old format or single team)
          const existingTeamId = typeof seasonData === 'string' 
            ? seasonData 
            : (seasonData.teamId || null);
          
          if (existingTeamId === teamId) {
            // Remove the team
            const updatedLeagueData = { ...leagueData };
            delete updatedLeagueData[seasonId];
            
            // If league has no more seasons, remove the league entry
            if (Object.keys(updatedLeagueData).length === 0) {
              const updatedTeams = { ...teams };
              delete updatedTeams[leagueId];
              await updateDoc(docRef, { teams: updatedTeams });
            } else {
              await updateDoc(docRef, {
                [`teams.${leagueId}`]: updatedLeagueData
              });
            }
          }
        }
      }
      // Keep team.players in sync with claims
      try {
        const teamRef = doc(
          db,
          'leagues',
          leagueId,
          'seasons',
          seasonId,
          'teams',
          teamId
        );
        await updateDoc(teamRef, {
          players: arrayRemove(userId),
          updatedAt: serverTimestamp()
        });
      } catch (err) {
        console.warn(
          'Could not remove user from team.players on unclaim:',
          err?.message || err
        );
      }
      return;
    }
    
    // Otherwise, search all leagues and seasons
    for (const lId in teams) {
      if (teams.hasOwnProperty(lId)) {
        const leagueData = teams[lId];
        for (const sId in leagueData) {
          if (leagueData.hasOwnProperty(sId)) {
            const seasonData = leagueData[sId];
            
            // Handle array format
            if (Array.isArray(seasonData)) {
              const teamIndex = seasonData.findIndex(t => {
                const existingTeamId = typeof t === 'string' ? t : (t.teamId || null);
                return existingTeamId === teamId;
              });
              
              if (teamIndex !== -1) {
                // Found it, remove from array
                const updatedArray = seasonData.filter((_, index) => index !== teamIndex);
                
                if (updatedArray.length === 0) {
                  // No teams left, remove the season entry
                  const updatedLeagueData = { ...leagueData };
                  delete updatedLeagueData[sId];
                  
                  // If league has no more seasons, remove the league entry
                  if (Object.keys(updatedLeagueData).length === 0) {
                    const updatedTeams = { ...teams };
                    delete updatedTeams[lId];
                    await updateDoc(docRef, { teams: updatedTeams });
                  } else {
                    await updateDoc(docRef, {
                      [`teams.${lId}`]: updatedLeagueData
                    });
                  }
                } else {
                  // Update with filtered array
                  await updateDoc(docRef, {
                    [`teams.${lId}.${sId}`]: updatedArray
                  });
                }
                return;
              }
            } else {
              // Handle single team format
              const existingTeamId = typeof seasonData === 'string' 
                ? seasonData 
                : (seasonData.teamId || null);
              
              if (existingTeamId === teamId) {
                // Found it, remove it
                const updatedLeagueData = { ...leagueData };
                delete updatedLeagueData[sId];
                
                // If league has no more seasons, remove the league entry
                if (Object.keys(updatedLeagueData).length === 0) {
                  const updatedTeams = { ...teams };
                  delete updatedTeams[lId];
                  await updateDoc(docRef, { teams: updatedTeams });
                } else {
                  await updateDoc(docRef, {
                    [`teams.${lId}`]: updatedLeagueData
                  });
                }
                return;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Get all teams claimed by a user
   * @param {string} userId - User document ID
   * @returns {Promise<Array>} - Array of { teamId, leagueId, seasonId, claimedAt } objects
   */
  static async getUserTeams(userId) {
    const userDoc = await this.getUser(userId);
    if (!userDoc || !userDoc.teams) return [];
    
    const { getUserClaimedTeams } = await import('../utils/teamUtils');
    return getUserClaimedTeams(userDoc.teams);
  }

  /**
   * Get all users who have claimed a specific team
   * @param {string} teamId - Team ID
   * @param {string} leagueId - League ID (optional, for faster lookup)
   * @param {string} seasonId - Season ID (optional, for faster lookup)
   * @returns {Promise<Array>} - Array of user objects who claimed the team
   */
  static async getUsersForTeam(teamId, leagueId = null, seasonId = null) {
    if (!teamId) return [];
    
    // Query all users (this is not efficient for large user bases, but Firestore doesn't support
    // querying nested map structures efficiently)
    const usersSnapshot = await getDocs(collection(db, this.collection));
    const users = [];
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const teams = userData.teams || {};
      
      // Helper function to check if a team value matches our teamId
      const matchesTeam = (teamValue) => {
        if (!teamValue) return false;
        if (typeof teamValue === 'string') {
          return teamValue === teamId;
        }
        if (teamValue.teamId) {
          return teamValue.teamId === teamId;
        }
        return false;
      };
      
      // If leagueId and seasonId provided, check specific location
      if (leagueId && seasonId) {
        const leagueData = teams[leagueId];
        if (leagueData) {
          const seasonData = leagueData[seasonId];
          if (seasonData) {
            // Handle array format (multiple teams per league-season)
            if (Array.isArray(seasonData)) {
              const hasTeam = seasonData.some(teamValue => matchesTeam(teamValue));
              if (hasTeam) {
                users.push({ id: userDoc.id, ...userData });
              }
            } else {
              // Handle single team format (old format or single team)
              if (matchesTeam(seasonData)) {
                users.push({ id: userDoc.id, ...userData });
              }
            }
          }
        }
      } else {
        // Search all leagues and seasons
        for (const lId in teams) {
          if (teams.hasOwnProperty(lId)) {
            const leagueData = teams[lId];
            for (const sId in leagueData) {
              if (leagueData.hasOwnProperty(sId)) {
                const seasonData = leagueData[sId];
                
                // Handle array format
                if (Array.isArray(seasonData)) {
                  const hasTeam = seasonData.some(teamValue => matchesTeam(teamValue));
                  if (hasTeam) {
                    users.push({ id: userDoc.id, ...userData });
                    break; // Found in this user, move to next user
                  }
                } else {
                  // Handle single team format
                  if (matchesTeam(seasonData)) {
                    users.push({ id: userDoc.id, ...userData });
                    break; // Found in this user, move to next user
                  }
                }
              }
            }
            if (users.find(u => u.id === userDoc.id)) break; // Already found this user
          }
        }
      }
    }
    
    return users;
  }



  /**
   * Add managed league to user
   * @param {string} userId - User document ID
   * @param {string} leagueId - League ID
   * @returns {Promise<void>}
   */
  static async addManagedLeague(userId, leagueId) {
    const docRef = doc(db, this.collection, userId);
    await updateDoc(docRef, {
      managedLeagues: arrayUnion(leagueId)
    });
  }
}

// ============================================================================
// SEASONS SERVICE
// ============================================================================

export class SeasonService {
  static collection = 'seasons';

  /**
   * Create a new season
   * @param {Object} seasonData - Season data
   * @returns {Promise<string>} - Season document ID
   */
  static async createSeason(seasonData) {
    const seasonDoc = {
      ...seasonData,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, this.collection), seasonDoc);
    return docRef.id;
  }

  /**
   * Get season by ID
   * @param {string} seasonId - Season document ID
   * @returns {Promise<Object>} - Season data
   */
  static async getSeason(seasonId) {
    const docRef = doc(db, this.collection, seasonId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }

  /**
   * Get all seasons
   * @returns {Promise<Array>} - Array of all seasons
   */
  static async getAllSeasons() {
    const querySnapshot = await getDocs(collection(db, this.collection));
    const seasons = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by createdAt in JavaScript
    return seasons.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return b.createdAt.toDate().getTime() - a.createdAt.toDate().getTime();
      }
      return 0;
    });
  }

  /**
   * Get all active seasons
   * @returns {Promise<Array>} - Array of active seasons
   */
  static async getActiveSeasons() {
    const q = query(
      collection(db, this.collection),
      where('status', '==', 'active')
    );
    
    const querySnapshot = await getDocs(q);
    const seasons = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by createdAt in JavaScript instead of Firestore query
    return seasons.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return b.createdAt.toDate().getTime() - a.createdAt.toDate().getTime();
      }
      return 0;
    });
  }

  /**
   * Update season data
   * @param {string} seasonId - Season document ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  static async updateSeason(seasonId, updates) {
    const docRef = doc(db, this.collection, seasonId);
    await updateDoc(docRef, updates);
  }

  /**
   * Add week results to season
   * @param {string} seasonId - Season document ID
   * @param {number} weekNumber - Week number
   * @param {Object} results - Week results
   * @returns {Promise<void>}
   */
  static async addWeekResults(seasonId, weekNumber, results) {
    const docRef = doc(db, this.collection, seasonId);
    await updateDoc(docRef, {
      [`weeks.${weekNumber - 1}.results`]: results
    });
  }

  /**
   * Check if a week is open for predictions
   * @param {Object} week - Week object
   * @returns {boolean} - True if week is open for predictions
   */
  static isWeekOpenForPredictions(week) {
    if (!week) return false;
    return week.isOpenForPredictions === true;
  }

  /**
   * Get open weeks for a season
   * @param {string} seasonId - Season document ID
   * @returns {Promise<Array>} - Array of open weeks
   */
  static async getOpenWeeks(seasonId) {
    const season = await this.getSeason(seasonId);
    if (!season || !season.weeks) return [];
    
    return season.weeks.filter(week => this.isWeekOpenForPredictions(week));
  }
}

// ============================================================================
// CONTESTANTS SERVICE
// ============================================================================

export class ContestantService {
  static collection = 'contestants';

  /**
   * Create a new contestant
   * @param {Object} contestantData - Contestant data
   * @returns {Promise<string>} - Contestant document ID
   */
  static async createContestant(contestantData) {
    const contestantDoc = {
      ...contestantData,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, this.collection), contestantDoc);
    return docRef.id;
  }

  /**
   * Get contestant by ID
   * @param {string} contestantId - Contestant document ID
   * @returns {Promise<Object>} - Contestant data
   */
  static async getContestant(contestantId) {
    const docRef = doc(db, this.collection, contestantId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }

  /**
   * Get all contestants
   * @returns {Promise<Array>} - Array of all contestants
   */
  static async getAllContestants() {
    const querySnapshot = await getDocs(collection(db, this.collection));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get contestants by season
   * @param {string} seasonId - Season ID
   * @returns {Promise<Array>} - Array of contestants
   */
  static async getContestantsBySeason(seasonId) {
    const q = query(
      collection(db, this.collection),
      where('seasons', 'array-contains', seasonId)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Update contestant data
   * @param {string} contestantId - Contestant document ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  static async updateContestant(contestantId, updates) {
    const docRef = doc(db, this.collection, contestantId);
    await updateDoc(docRef, updates);
  }
}

// ============================================================================
// LEAGUES SERVICE
// ============================================================================

export class LeagueService {
  static collection = 'leagues';

  /**
   * Create a new league
   * @param {Object} leagueData - League data
   * @param {string} managerId - Manager user ID
   * @returns {Promise<string>} - League document ID
   */
  static async createLeague(leagueData, managerId) {
    const leagueDoc = {
      ...leagueData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Only add managerId if it's provided
    if (managerId && managerId.trim() !== '') {
      leagueDoc.managerId = managerId;
    }

    const docRef = await addDoc(collection(db, this.collection), leagueDoc);
    
    // Add league to manager's managed leagues only if manager is provided
    if (managerId && managerId.trim() !== '') {
      await UserService.addManagedLeague(managerId, docRef.id);
    }
    
    return docRef.id;
  }

  /**
   * Whether a league document should appear in UI lists and lookups.
   * @param {Object|null|undefined} league - League document data
   * @returns {boolean}
   */
  static isLeagueVisible(league) {
    return Boolean(league && league.isDeleted !== true);
  }

  /**
   * Get league by ID
   * @param {string} leagueId - League document ID
   * @param {Object} [options]
   * @param {boolean} [options.includeDeleted=false] - Return soft-deleted leagues
   * @returns {Promise<Object|null>} - League data
   */
  static async getLeague(leagueId, { includeDeleted = false } = {}) {
    const docRef = doc(db, this.collection, leagueId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const league = { id: docSnap.id, ...docSnap.data() };
    if (!includeDeleted && !this.isLeagueVisible(league)) {
      return null;
    }
    return league;
  }

  /**
   * Get all leagues
   * @param {Object} [options]
   * @param {boolean} [options.includeDeleted=false] - Include soft-deleted leagues
   * @returns {Promise<Array>} - Array of all leagues
   */
  static async getAllLeagues({ includeDeleted = false } = {}) {
    const querySnapshot = await getDocs(collection(db, this.collection));
    const leagues = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    if (includeDeleted) {
      return leagues;
    }
    return leagues.filter(league => this.isLeagueVisible(league));
  }

  /**
   * Get all leagues that have a league-season for the given global season.
   * @param {string} seasonId - Global season ID (seasons collection doc id)
   * @returns {Promise<Array<{ id: string, leagueSeason: Object, teamCount: number }>>}
   */
  static async getLeaguesUsingSeason(seasonId) {
    const leagues = await this.getAllLeagues();
    const results = [];

    for (const league of leagues) {
      const seasonRef = doc(db, 'leagues', league.id, 'seasons', seasonId);
      const seasonSnap = await getDoc(seasonRef);
      if (!seasonSnap.exists()) continue;

      const teamCount = await TeamService.getTeamCount(league.id, seasonId);
      results.push({
        ...league,
        leagueSeason: { id: seasonSnap.id, ...seasonSnap.data() },
        teamCount
      });
    }

    return results.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  /**
   * Get leagues managed by user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} - Array of managed leagues
   */
  static async getManagedLeagues(userId) {
    const q = query(
      collection(db, this.collection),
      where('managerId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(league => this.isLeagueVisible(league));
  }

  /**
   * Update league data
   * @param {string} leagueId - League document ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  static async updateLeague(leagueId, updates) {
    const docRef = doc(db, this.collection, leagueId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Soft-delete league (keeps all subcollection data in Firestore)
   * @param {string} leagueId - League document ID
   * @returns {Promise<void>}
   */
  static async softDeleteLeague(leagueId) {
    const docRef = doc(db, this.collection, leagueId);
    await updateDoc(docRef, {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Delete league
   * @param {string} leagueId - League document ID
   * @returns {Promise<void>}
   */
  static async deleteLeague(leagueId) {
    const docRef = doc(db, this.collection, leagueId);
    await deleteDoc(docRef);
  }

  /**
   * Create a league season
   * @param {string} leagueId - League ID
   * @param {Object} seasonData - Season data
   * @returns {Promise<void>}
   */
  static async createLeagueSeason(leagueId, seasonData) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonData.seasonId);
    await setDoc(docRef, seasonData);
  }

  /**
   * Get all seasons for a league
   * @param {string} leagueId - League ID
   * @returns {Promise<Array>} - Array of league seasons
   */
  static async getLeagueSeasons(leagueId) {
    const seasonsRef = collection(db, 'leagues', leagueId, 'seasons');
    const querySnapshot = await getDocs(seasonsRef);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get current league season
   * @param {string} leagueId - League ID
   * @returns {Promise<Object>} - Current season data
   */
  static async getCurrentLeagueSeason(leagueId) {
    const seasonsRef = collection(db, 'leagues', leagueId, 'seasons');
    const q = query(seasonsRef, where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Sort by createdAt in JavaScript to avoid index requirement
      const seasons = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const sortedSeasons = seasons.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toDate().getTime() - a.createdAt.toDate().getTime();
        }
        return 0;
      });
      
      return sortedSeasons[0];
    }
    return null;
  }

  /**
   * Start draft for a league season
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<void>}
   */
  static async startDraft(leagueId, seasonId) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId);
    await updateDoc(docRef, {
      draftStatus: 'inProgress',
      draftStartTime: new Date()
    });
  }

  /**
   * Renew league for next season
   * @param {string} leagueId - League ID
   * @returns {Promise<void>}
   */
  static async renewLeague(leagueId) {
    // This would be implemented to copy teams to a new season
    // For now, just update the current season
    console.log('Renew league functionality to be implemented');
  }


  /**
   * Add manager to league
   * @param {string} leagueId - League ID
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  static async addManagerToLeague(leagueId, userId) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      managedLeagues: arrayUnion(leagueId)
    });
  }
}

// ============================================================================
// LEAGUE SEASONS SERVICE
// ============================================================================

export class LeagueSeasonService {
  /**
   * Create a new league season
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {Object} seasonData - League season data
   * @returns {Promise<void>}
   */
  static async createLeagueSeason(leagueId, seasonId, seasonData) {
    const { generateInvitationCode } = await import('../models/dataModels.js');
    
    const defaultScoringRules = {
      postMergePredictionPoints: 3,
      preMergePredictionPoints: 3,
      survivalPoints: 1,
      winnerPoints: 1
    };
    const leagueSeasonDoc = {
      seasonId,
      invitationCode: generateInvitationCode(),
      draftStatus: 'notStarted',
      scoringRules: defaultScoringRules,
      draftOrder: [],
      currentDraftRound: 1,
      currentDraftPick: 1,
      isActive: true,
      ...seasonData
    };

    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId);
    await setDoc(docRef, leagueSeasonDoc);
  }

  /**
   * Get league season data
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<Object>} - League season data
   */
  static async getLeagueSeason(leagueId, seasonId) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }

  /**
   * Update league season data
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  static async updateLeagueSeason(leagueId, seasonId, updates) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId);
    await updateDoc(docRef, updates);
  }

  /**
   * Update isActive for all league-seasons that reference a global season.
   * Called when an admin sets the global season to complete or reactivates it.
   * @param {string} globalSeasonId - Global season ID (from seasons collection)
   * @param {boolean} isActive - New isActive value
   * @returns {Promise<void>}
   */
  static async updateIsActiveForGlobalSeason(globalSeasonId, isActive) {
    const leagues = await LeagueService.getAllLeagues();
    for (const league of leagues) {
      const docRef = doc(db, 'leagues', league.id, 'seasons', globalSeasonId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        await updateDoc(docRef, { isActive });
      }
    }
  }

  /**
   * Update scoring rules
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {Object} scoringRules - New scoring rules
   * @param {string} changedBy - User ID who made the change
   * @returns {Promise<void>}
   */
  static async updateScoringRules(leagueId, seasonId, scoringRules, changedBy) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId);
    await updateDoc(docRef, { scoringRules });

    // Log the change
    await LogService.createLog({
      leagueId,
      seasonId,
      changeMade: 'Updated scoring rules',
      changedBy,
      changeType: 'scoring',
      oldValue: null, // Could store previous rules if needed
      newValue: scoringRules
    });
  }
}

// ============================================================================
// TEAMS SERVICE
// ============================================================================

export class TeamService {
  /**
   * Create a new team
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {Object} teamData - Team data
   * @returns {Promise<string>} - Team document ID
   */
  static async createTeam(leagueId, seasonId, teamData) {
    const teamDoc = {
      ...teamData,
      draftPicks: [],
      currentScore: 0,
      weeklyScores: {},
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(
      collection(db, 'leagues', leagueId, 'seasons', seasonId, 'teams'),
      teamDoc
    );

    // Add team to league season's draft order
    await LeagueSeasonService.updateLeagueSeason(leagueId, seasonId, {
      draftOrder: arrayUnion(docRef.id)
    });

    return docRef.id;
  }

  /**
   * Get team by ID
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} - Team data
   */
  static async getTeam(leagueId, seasonId, teamId) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId, 'teams', teamId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }

  /**
   * Get the count of teams for a league season (uses aggregation; does not read document data).
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<number>} - Number of teams
   */
  static async getTeamCount(leagueId, seasonId) {
    const coll = collection(db, 'leagues', leagueId, 'seasons', seasonId, 'teams');
    const snapshot = await getCountFromServer(coll);
    return snapshot.data().count;
  }

  /**
   * Get all teams for a league season
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<Array>} - Array of teams
   */
  static async getTeams(leagueId, seasonId) {
    const querySnapshot = await getDocs(
      collection(db, 'leagues', leagueId, 'seasons', seasonId, 'teams')
    );
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Update team data
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {string} teamId - Team ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  static async updateTeam(leagueId, seasonId, teamId, updates) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId, 'teams', teamId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Add draft pick to team
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {string} teamId - Team ID
   * @param {Object} draftPick - Draft pick data
   * @returns {Promise<void>}
   */
  static async addDraftPick(leagueId, seasonId, teamId, draftPick) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId, 'teams', teamId);
    await updateDoc(docRef, {
      draftPicks: arrayUnion(draftPick)
    });
  }

  /**
   * Get teams for a league season
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<Array>} - Array of teams
   */
  static async getTeamsForLeagueSeason(leagueId, seasonId) {
    const querySnapshot = await getDocs(
      collection(db, 'leagues', leagueId, 'seasons', seasonId, 'teams')
    );
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get teams for a specific league season (alias for getTeamsForLeagueSeason)
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<Array>} - Array of teams
   */
  static async getLeagueSeasonTeams(leagueId, seasonId) {
    return this.getTeamsForLeagueSeason(leagueId, seasonId);
  }

  /**
   * Calculate and update team score for a week
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {string} teamId - Team ID
   * @param {number} week - Week number
   * @param {Object} predictions - Team's predictions
   * @param {Object} results - Actual results
   * @param {Object} scoringRules - League scoring rules
   * @returns {Promise<void>}
   */
  static async updateTeamScore(leagueId, seasonId, teamId, week, predictions, results, scoringRules) {
    // TODO: Implement score calculation using ScoreService
    // const weeklyScore = calculateWeeklyScore(predictions, results, scoringRules);
    
    // const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId, 'teams', teamId);
    // await updateDoc(docRef, {
    //   [`weeklyScores.${week}`]: weeklyScore,
    //   currentScore: increment(weeklyScore)
    // });
  }

  /**
   * Get team claiming status
   * Checks if a team is claimed and by whom
   * @param {string} teamId - Team ID
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<Object>} - { isClaimed: boolean, claimedBy: Array of user objects, claimCount: number }
   */
  static async getTeamClaimingStatus(teamId, leagueId, seasonId) {
    if (!teamId || !leagueId || !seasonId) {
      return { isClaimed: false, claimedBy: [], claimCount: 0 };
    }

    const users = await UserService.getUsersForTeam(teamId, leagueId, seasonId);
    
    return {
      isClaimed: users.length > 0,
      claimedBy: users,
      claimCount: users.length
    };
  }

  /**
   * Get claiming status for multiple teams
   * Batch check multiple teams for claiming status
   * @param {Array<string>} teamIds - Array of team IDs
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<Object>} - Map of teamId -> { isClaimed: boolean, claimedBy: Array, claimCount: number }
   */
  static async getTeamsClaimingStatus(teamIds, leagueId, seasonId) {
    if (!teamIds || teamIds.length === 0 || !leagueId || !seasonId) {
      return {};
    }

    const statusMap = {};
    
    // Get all users and their teams for this league-season
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const teamToUsersMap = {};
    
    // Build a map of teamId -> users who claimed it
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const teams = userData.teams || {};
      const leagueData = teams[leagueId];
      
      if (leagueData) {
        const seasonData = leagueData[seasonId];
        if (seasonData) {
          // Handle array format (multiple teams per league-season)
          if (Array.isArray(seasonData)) {
            seasonData.forEach(teamValue => {
              const teamId = typeof teamValue === 'string' 
                ? teamValue 
                : (teamValue?.teamId || null);
              
              if (teamId && teamIds.includes(teamId)) {
                if (!teamToUsersMap[teamId]) {
                  teamToUsersMap[teamId] = [];
                }
                teamToUsersMap[teamId].push({ id: userDoc.id, ...userData });
              }
            });
          } else {
            // Handle single team format (old format or single team)
            const existingTeamId = typeof seasonData === 'string' 
              ? seasonData 
              : (seasonData.teamId || null);
            
            if (existingTeamId && teamIds.includes(existingTeamId)) {
              if (!teamToUsersMap[existingTeamId]) {
                teamToUsersMap[existingTeamId] = [];
              }
              teamToUsersMap[existingTeamId].push({ id: userDoc.id, ...userData });
            }
          }
        }
      }
    }
    
    // Build status map
    for (const teamId of teamIds) {
      const users = teamToUsersMap[teamId] || [];
      statusMap[teamId] = {
        isClaimed: users.length > 0,
        claimedBy: users,
        claimCount: users.length
      };
    }
    
    return statusMap;
  }
}

// ============================================================================
// PREDICTIONS SERVICE
// ============================================================================

export class PredictionService {
  static collection = 'predictions';

  /**
   * Create a new prediction
   * @param {Object} predictionData - Prediction data
   * @param {string} submittedBy - User who submitted the prediction
   * @param {string} reason - Reason for the prediction (optional)
   * @returns {Promise<string>} - Prediction document ID
   */
  static async createPrediction(predictionData, submittedBy, reason = null) {
    const predictionDoc = {
      ...predictionData,
      timestamp: serverTimestamp(),
      isLocked: false
    };

    const docRef = await addDoc(collection(db, this.collection), predictionDoc);
    
    // Create history entry for the new prediction
    await PredictionHistoryService.createHistoryEntry(
      docRef.id,
      'create',
      null, // No old data for create
      predictionDoc,
      submittedBy,
      reason
    );
    
    return docRef.id;
  }

  /**
   * Get prediction by ID
   * @param {string} predictionId - Prediction document ID
   * @returns {Promise<Object>} - Prediction data
   */
  static async getPrediction(predictionId) {
    const docRef = doc(db, this.collection, predictionId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }

  /**
   * Get team prediction for a specific week
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {string} teamId - Team ID
   * @param {number} week - Week number
   * @returns {Promise<Object>} - Prediction data
   */
  static async getTeamPrediction(leagueId, seasonId, teamId, week) {
    const q = query(
      collection(db, this.collection),
      where('leagueId', '==', leagueId),
      where('seasonId', '==', seasonId),
      where('teamId', '==', teamId),
      where('weekNumber', '==', week),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  }

  /**
   * Update prediction
   * @param {string} predictionId - Prediction document ID
   * @param {Object} updates - Fields to update
   * @param {string} submittedBy - User who made the update
   * @param {string} reason - Reason for the update (optional)
   * @returns {Promise<void>}
   */
  static async updatePrediction(predictionId, updates, submittedBy, reason = null) {
    // Get current data before update
    const currentDoc = await getDoc(doc(db, this.collection, predictionId));
    const oldData = currentDoc.exists() ? currentDoc.data() : null;
    
    // Perform the update
    const docRef = doc(db, this.collection, predictionId);
    await updateDoc(docRef, updates);
    
    // Get updated data
    const updatedDoc = await getDoc(docRef);
    const newData = updatedDoc.exists() ? updatedDoc.data() : null;
    
    // Create history entry for the update
    await PredictionHistoryService.createHistoryEntry(
      predictionId,
      'update',
      oldData,
      newData,
      submittedBy,
      reason
    );
  }

  /**
   * Get predictions for a team in a specific week
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {string} teamId - Team ID
   * @param {number} week - Week number
   * @returns {Promise<Array>} - Array of predictions
   */
  static async getTeamPredictions(leagueId, seasonId, teamId, week) {
    const q = query(
      collection(db, this.collection),
      where('leagueId', '==', leagueId),
      where('seasonId', '==', seasonId),
      where('teamId', '==', teamId),
      where('weekNumber', '==', week)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get all predictions for a team across all weeks
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {string} teamId - Team ID
   * @returns {Promise<Array>} - Array of predictions sorted by week number
   */
  static async getAllTeamPredictions(leagueId, seasonId, teamId) {
    // Query without orderBy to avoid index requirement
    // We'll sort in JavaScript instead
    const q = query(
      collection(db, this.collection),
      where('leagueId', '==', leagueId),
      where('seasonId', '==', seasonId),
      where('teamId', '==', teamId)
    );
    
    const querySnapshot = await getDocs(q);
    const predictions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by weekNumber in JavaScript (ascending)
    return predictions.sort((a, b) => (a.weekNumber || 0) - (b.weekNumber || 0));
  }

  /**
   * Get all predictions for a league in a specific week
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {number} week - Week number
   * @returns {Promise<Array>} - Array of predictions
   */
  static async getLeaguePredictions(leagueId, seasonId, week) {
    const q = query(
      collection(db, this.collection),
      where('leagueId', '==', leagueId),
      where('seasonId', '==', seasonId),
      where('weekNumber', '==', week)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get all predictions for a league and season (all weeks)
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @returns {Promise<Array>} - Array of all predictions
   */
  static async getAllLeaguePredictions(leagueId, seasonId) {
    const q = query(
      collection(db, this.collection),
      where('leagueId', '==', leagueId),
      where('seasonId', '==', seasonId)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get all predictions for a specific season and week across all leagues
   * @param {string} seasonId - Season ID
   * @param {number} weekNumber - Week number
   * @returns {Promise<Array>} - Array of all predictions
   */
  static async getPredictionsBySeasonAndWeek(seasonId, weekNumber) {
    const q = query(
      collection(db, this.collection),
      where('seasonId', '==', seasonId),
      where('weekNumber', '==', weekNumber)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Lock predictions for a week (after deadline)
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {number} week - Week number
   * @returns {Promise<void>}
   */
  static async lockPredictions(leagueId, seasonId, week) {
    const predictions = await this.getLeaguePredictions(leagueId, seasonId, week);
    
    const batch = writeBatch(db);
    predictions.forEach(prediction => {
      const docRef = doc(db, this.collection, prediction.id);
      batch.update(docRef, { isLocked: true });
    });
    
    await batch.commit();
  }
}

// ============================================================================
// LOG SERVICE
// ============================================================================

export class LogService {
  static collection = 'log';

  /**
   * Create a new log entry
   * @param {Object} logData - Log data
   * @returns {Promise<string>} - Log document ID
   */
  static async createLog(logData) {
    const logDoc = {
      ...logData,
      timestamp: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, this.collection), logDoc);
    return docRef.id;
  }

  /**
   * Get logs for a league
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID (optional)
   * @returns {Promise<Array>} - Array of log entries
   */
  static async getLeagueLogs(leagueId, seasonId = null) {
    let q = query(
      collection(db, this.collection),
      where('leagueId', '==', leagueId),
      orderBy('timestamp', 'desc')
    );

    if (seasonId) {
      q = query(q, where('seasonId', '==', seasonId));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
}

// ============================================================================
// PREDICTION HISTORY SERVICE
// ============================================================================

export class PredictionHistoryService {
  static collection = 'predictionHistory';

  /**
   * Create a history entry for a prediction
   * @param {string} predictionId - Prediction ID
   * @param {string} action - Action type (create, update, delete)
   * @param {Object} oldData - Previous prediction data (null for create)
   * @param {Object} newData - New prediction data
   * @param {string} submittedBy - User who made the change
   * @param {string} reason - Reason for the change
   * @returns {Promise<string>} - History entry ID
   */
  static async createHistoryEntry(predictionId, action, oldData, newData, submittedBy, reason = null) {
    const historyEntry = {
      action,
      oldData,
      newData,
      submittedBy,
      reason: reason || this.getDefaultReason(action),
      timestamp: serverTimestamp()
    };

    const docRef = await addDoc(
      collection(db, 'predictions', predictionId, 'predictionHistory'), 
      historyEntry
    );
    return docRef.id;
  }

  /**
   * Get prediction history
   * @param {string} predictionId - Prediction ID
   * @returns {Promise<Array>} - Array of history entries sorted by timestamp
   */
  static async getPredictionHistory(predictionId) {
    const q = query(
      collection(db, 'predictions', predictionId, 'predictionHistory'),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get prediction history by action type
   * @param {string} predictionId - Prediction ID
   * @param {string} action - Action type to filter by
   * @returns {Promise<Array>} - Array of filtered history entries
   */
  static async getPredictionHistoryByAction(predictionId, action) {
    const q = query(
      collection(db, 'predictions', predictionId, 'predictionHistory'),
      where('action', '==', action),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get default reason for action
   * @param {string} action - Action type
   * @returns {string} - Default reason
   */
  static getDefaultReason(action) {
    const reasons = {
      'create': 'Initial submission',
      'update': 'Updated prediction',
      'delete': 'Prediction deleted'
    };
    return reasons[action] || 'Unknown action';
  }
}

// ============================================================================
// REAL-TIME LISTENERS
// ============================================================================

export class RealtimeService {
  /**
   * Listen to league changes
   * @param {string} leagueId - League ID
   * @param {Function} callback - Callback function
   * @returns {Function} - Unsubscribe function
   */
  static listenToLeague(leagueId, callback) {
    const docRef = doc(db, 'leagues', leagueId);
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    });
  }

  /**
   * Listen to team changes
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {string} teamId - Team ID
   * @param {Function} callback - Callback function
   * @returns {Function} - Unsubscribe function
   */
  static listenToTeam(leagueId, seasonId, teamId, callback) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId, 'teams', teamId);
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    });
  }

  /**
   * Listen to league season changes
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {Function} callback - Callback function
   * @returns {Function} - Unsubscribe function
   */
  static listenToLeagueSeason(leagueId, seasonId, callback) {
    const docRef = doc(db, 'leagues', leagueId, 'seasons', seasonId);
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    });
  }
}

export default {
  RoleService,
  UserService,
  SeasonService,
  ContestantService,
  LeagueService,
  LeagueSeasonService,
  TeamService,
  PredictionService,
  PredictionHistoryService,
  LogService,
  RealtimeService
};
